import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const CAREERS = ["Engineering?", "Law?", "Medicine?", "Defence?", "Design?", "Finance?", "Something else entirely?"];

const RADIUS = 118;
const MARKS = [
  { angle: 0, size: "text-3xl sm:text-4xl", color: "text-indigo-500", duration: 16 },
  { angle: 72, size: "text-2xl sm:text-3xl", color: "text-cyan-500", duration: 16 },
  { angle: 144, size: "text-4xl sm:text-5xl", color: "text-violet-500", duration: 16 },
  { angle: 216, size: "text-2xl sm:text-3xl", color: "text-amber-500", duration: 16 },
  { angle: 288, size: "text-xl sm:text-2xl", color: "text-rose-400", duration: 16 },
];

const orbitPoints = (startAngleDeg) => {
  const steps = 24;
  const xs = [];
  const ys = [];
  for (let i = 0; i <= steps; i++) {
    const angle = ((startAngleDeg + (360 * i) / steps) * Math.PI) / 180;
    xs.push(Math.round(RADIUS * Math.cos(angle)));
    ys.push(Math.round(RADIUS * Math.sin(angle) * 0.85));
  }
  return { xs, ys };
};

export default function ConfusionSection() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % CAREERS.length), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="bg-gradient-to-b from-indigo-50 via-sky-50/60 to-white py-16 sm:py-24 px-6 text-center border-b border-slate-100 overflow-hidden" data-testid="confusion-section">
      <p className="text-sm sm:text-base uppercase tracking-[0.2em] font-bold text-indigo-600 mb-8">A Question Every Student Asks</p>

      <div className="relative mx-auto w-64 h-64 sm:w-72 sm:h-72 mb-8" data-testid="confusion-thinking-visual">
        <motion.div
          className="absolute inset-6 rounded-full bg-gradient-to-br from-indigo-300/30 via-sky-300/25 to-cyan-300/30 blur-2xl"
          animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/fba0d12f-f224-488d-926e-6afdb5e36f91/images/9dfaa0fee75fb8eb92bf50daa0421d4656bc69542cf00bc5c331345c81d3fe92.jpeg"
            alt="Student scratching head, confused about which career path to choose"
            className="h-52 sm:h-60 w-52 sm:w-60 object-cover rounded-full shadow-xl ring-4 ring-white"
          />
        </div>
        {MARKS.map((m, i) => {
          const { xs, ys } = orbitPoints(m.angle);
          return (
            <motion.span
              key={i}
              className={`absolute top-1/2 left-1/2 font-heading font-black select-none -translate-x-1/2 -translate-y-1/2 ${m.size} ${m.color}`}
              animate={{ x: xs, y: ys }}
              transition={{ duration: m.duration, repeat: Infinity, ease: "linear" }}
              data-testid={`confusion-floating-mark-${i}`}
            >
              ?
            </motion.span>
          );
        })}
      </div>

      <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 max-w-3xl mx-auto leading-snug">
        "Which career path is really right for me —{" "}
        <span className="relative inline-flex align-bottom justify-center min-w-[3ch]">
          <AnimatePresence mode="wait">
            <motion.span
              key={CAREERS[index]}
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="inline-block whitespace-nowrap text-indigo-600"
              data-testid="confusion-rotating-word"
            >
              {CAREERS[index]}
            </motion.span>
          </AnimatePresence>
        </span>
        "
      </h2>
      <p className="mt-8 text-sm sm:text-base text-slate-500 max-w-xl mx-auto leading-relaxed">
        It's one of the biggest decisions you'll make, and it's completely normal to feel unsure. Marks alone can't
        answer it — and neither can a single quiz. CareerStudy360 looks at the fuller picture of who you are before
        you have to decide.
      </p>
    </section>
  );
}
